import ReportToolbarButton from '../../shared/ReportToolbarButton';
import { Typography, Tab, Box, Tooltip, useTheme, TableCell, IconButton, Skeleton, Stack, Divider, Card, CardContent, Button, Dialog, DialogTitle, DialogContent, DialogActions, Alert, ThemeProvider, alpha } from '@mui/material';
import { useState, useEffect, useContext, useRef, useMemo } from 'react';
import ViMarcTableCard from '../../../system/vimarc-table-card';
import ResponsiveTabs from '../../../system/responsive-tabs';
import { generatePDF } from '../../../dwi-functions';
import Comments from '../../../views/comments';
import { AppVarsContext } from '../../../context/AppVarsContext';
import { useToken } from '../../../Login/TokenProvider';
import { MdPhoneIphone, MdOutlinePhoneAndroid, MdTabletAndroid, MdFilterAlt } from "react-icons/md";
import { FcAndroidOs } from "react-icons/fc";
import { IoIosTabletLandscape } from "react-icons/io";
import { IoDesktop, IoLogoApple } from "react-icons/io5";
import CreativeDetailTabs from './CreativeDetailsTab';
import LoadingDataBox from '../../../system/loading-data-box';
import TrendChart from '../../../report-components/trend-chart';
import ReactApexChart from 'react-apexcharts';
import ReportConversionSelect from '../../../system/controls/report-conversions';
import { applyTrendTooltip } from '../../../system/apex-tooltip';
import { useUserProfile } from '../../../context/UserProvider';
import ReportPerformanceOverview, { ReportSectionIntro } from '../../shared/ReportPerformanceOverview';
import { createReportSurfaceTheme, getReportPageSx, getReportSurfaceSx, getAnalyticsReportTabsSx, reportColors, styleReportChart } from '../../shared/report-design';
import MetaEngagementSection, { getMetaEngagementColumns } from '../../shared/metaEngagementSection';

import { normalizeReportProfileVisibility } from '../../../system/report-visibility';


function TabPanel(props) {
    const { children, value, index, ...other } = props;
    if (value !== index) return null;
    return (
        <Box role="tabpanel" {...other}>
            {children}
        </Box>
    );
}

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

const normalizeDeviceKey = (value) => String(value || '').toLowerCase();
const deviceLabelMap = {
    iphone: 'iPhone',
    android_smartphone: 'Android Phone',
    android_tablet: 'Android Tablet',
    ipad: 'iPad',
    desktop: 'Desktop',
    unknown: 'Unknown'
};

const formatNumber = (value) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(value || 0));
const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;
const truncateText = (value, max = 28) => {
    const text = String(value || '');
    if (text.length <= max) return text;
    return `${text.slice(0, max - 3)}...`;
};

const getNumericValue = (row, keys) => {
    for (const key of keys) {
        const value = row?.[key];
        if (value === undefined || value === null || value === '') continue;
        const numeric = Number(value);
        if (Number.isFinite(numeric)) return numeric;
    }
    return 0;
};

const hasRowValue = (row, keys) => (
    keys.some((key) => row?.[key] !== undefined && row?.[key] !== null && row?.[key] !== '')
);

const resolveRowLabel = (row, keys) => {
    for (const key of keys) {
        const value = String(row?.[key] || '').trim();
        if (value) return value;
    }
    return 'Unknown';
};

const getMetaEngagementMetrics = (row) => {
    const reactionKeys = ['REACTIONS', 'reactions', 'POST_REACTIONS', 'post_reactions', 'LIKES', 'likes'];
    const commentKeys = ['COMMENTS', 'comments', 'POST_COMMENTS', 'post_comments'];
    const shareKeys = ['SHARES', 'shares', 'POST_SHARES', 'post_shares'];
    const saveKeys = ['SAVES', 'saves', 'POST_SAVES', 'post_saves'];
    const totalKeys = ['ENGAGEMENT', 'engagement', 'TOTAL_ENGAGEMENT', 'total_engagement', 'POST_ENGAGEMENT', 'post_engagement'];

    const reactions = getNumericValue(row, reactionKeys);
    const comments = getNumericValue(row, commentKeys);
    const shares = getNumericValue(row, shareKeys);
    const saves = getNumericValue(row, saveKeys);
    const explicitTotal = getNumericValue(row, totalKeys);
    const summedTotal = reactions + comments + shares + saves;

    return {
        reactions,
        comments,
        shares,
        saves,
        engagementTotal: explicitTotal > 0 ? explicitTotal : summedTotal,
        hasEngagementSignal: hasRowValue(row, [...reactionKeys, ...commentKeys, ...shareKeys, ...saveKeys, ...totalKeys])
    };
};

const buildMetaSummaryRow = (row, labelKeys) => {
    const impressions = getNumericValue(row, ['IMPRESSIONS', 'impressions']);
    const clicks = getNumericValue(row, ['CLICKS', 'clicks']);
    const conversions = getNumericValue(row, ['CONVERSIONS', 'conversions']);
    const cost = getNumericValue(row, ['COST', 'cost']);
    const revenue = getNumericValue(row, ['REVENUE', 'revenue']);
    const ctr = getNumericValue(row, ['CTR', 'ctr']) || (impressions > 0 ? (clicks / impressions) * 100 : 0);
    const convRate =
        getNumericValue(row, ['CONV_RATE', 'CONVRATE', 'convrate', 'convRate']) ||
        (clicks > 0 ? (conversions / clicks) * 100 : 0);
    const roas = cost > 0 ? revenue / cost : 0;
    const engagement = getMetaEngagementMetrics(row);

    return {
        label: resolveRowLabel(row, labelKeys),
        impressions,
        clicks,
        conversions,
        cost,
        revenue,
        ctr,
        convRate,
        roas,
        ...engagement,
        engagementRate: impressions > 0 ? (engagement.engagementTotal / impressions) * 100 : 0,
        raw: row
    };
};

const normalizePayloadRows = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.rows)) return payload.rows;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.results)) return payload.results;
    return [];
};

const buildMetaEngagementTotals = (totalsPayload, rows) => {
    const fallbackTotals = rows.reduce((acc, row) => {
        acc.impressions += row.impressions || 0;
        acc.clicks += row.clicks || 0;
        acc.reactions += row.reactions || 0;
        acc.comments += row.comments || 0;
        acc.shares += row.shares || 0;
        acc.saves += row.saves || 0;
        acc.engagementTotal += row.engagementTotal || 0;
        return acc;
    }, {
        impressions: 0,
        clicks: 0,
        reactions: 0,
        comments: 0,
        shares: 0,
        saves: 0,
        engagementTotal: 0
    });

    if (!totalsPayload || typeof totalsPayload !== 'object') {
        return fallbackTotals;
    }

    const metrics = getMetaEngagementMetrics(totalsPayload);

    return {
        impressions: hasRowValue(totalsPayload, ['IMPRESSIONS', 'impressions'])
            ? getNumericValue(totalsPayload, ['IMPRESSIONS', 'impressions'])
            : fallbackTotals.impressions,
        clicks: hasRowValue(totalsPayload, ['CLICKS', 'clicks'])
            ? getNumericValue(totalsPayload, ['CLICKS', 'clicks'])
            : fallbackTotals.clicks,
        reactions: hasRowValue(totalsPayload, ['REACTIONS', 'reactions', 'POST_REACTIONS', 'post_reactions', 'LIKES', 'likes'])
            ? metrics.reactions
            : fallbackTotals.reactions,
        comments: hasRowValue(totalsPayload, ['COMMENTS', 'comments', 'POST_COMMENTS', 'post_comments'])
            ? metrics.comments
            : fallbackTotals.comments,
        shares: hasRowValue(totalsPayload, ['SHARES', 'shares', 'POST_SHARES', 'post_shares'])
            ? metrics.shares
            : fallbackTotals.shares,
        saves: hasRowValue(totalsPayload, ['SAVES', 'saves', 'POST_SAVES', 'post_saves'])
            ? metrics.saves
            : fallbackTotals.saves,
        engagementTotal: hasRowValue(totalsPayload, ['ENGAGEMENT', 'engagement', 'TOTAL_ENGAGEMENT', 'total_engagement', 'POST_ENGAGEMENT', 'post_engagement'])
            ? metrics.engagementTotal
            : fallbackTotals.engagementTotal
    };
};

const normalizeMetaEngagementPayload = (payload) => {
    const rows = normalizePayloadRows(payload).map((row) => (
        buildMetaSummaryRow(row, ['ADSET', 'AD_SET', 'ADSET_NAME', 'AD_SET_NAME', 'NAME', 'CAMPAIGN', 'CAMPAIGN_NAME'])
    ));
    const totalsPayload = Array.isArray(payload) ? null : (payload?.totals || payload?.summary || payload?.total || null);

    return {
        rows,
        totals: buildMetaEngagementTotals(totalsPayload, rows)
    };
};

const ensureApexOptions = (options, chartType, theme) => {
    const base = options && typeof options === 'object' ? options : {};
    const merged = styleReportChart(theme, {
        colors: reportColors.categories,
        ...base,
        chart: {
            zoom: { enabled: false },
            toolbar: { show: false },
            ...(base.chart || {}),
            ...(chartType ? { type: chartType } : {})
        }
    });
    return applyTrendTooltip(theme, merged);
};

const parseSettings = (raw) => {
    if (!raw) return null;
    if (typeof raw === 'object') return raw;
    if (typeof raw === 'string') {
        try {
            return JSON.parse(raw);
        } catch (error) {
            return null;
        }
    }
    return null;
};

const hasConversionSpecValue = (value) => {
    if (value == null) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Object.keys(value).length > 0;
    if (typeof value === 'string') return value.trim().length > 0;
    if (typeof value === 'number') return Number.isFinite(value);
    return false;
};

const pickMetaConversionSpec = (settings) => {
    if (!settings || typeof settings !== 'object') return null;
    const candidates = [
        settings?.conversion_defaults?.meta,
        settings?.conversionActions?.meta,
        settings?.conversion_actions?.meta,
        settings?.conversionSpec?.meta,
        settings?.conversion_spec?.meta,
        settings?.conversions?.meta,
        settings?.conversionActions,
        settings?.conversion_actions,
        settings?.conversionSpec,
        settings?.conversion_spec,
        settings?.conversions,
        settings?.ad_sets?.meta?.conversionActions,
        settings?.ad_sets?.meta?.conversion_actions,
        settings?.ad_sets?.meta?.conversionSpec,
        settings?.ad_sets?.meta?.conversion_spec,
        settings?.ad_sets?.meta?.conversions
    ];
    return candidates.find(hasConversionSpecValue) || null;
};



const Meta = (props) => {
    const { user } = useUserProfile();
    const pDate = new Intl.DateTimeFormat('en-US', { month: 'long', day: '2-digit', year: 'numeric', timeZone: "UTC" }).format(new Date(sessionStorage.getItem('sDate'))) + "-" + new Intl.DateTimeFormat('en-US', { month: 'long', day: '2-digit', year: 'numeric', timeZone: "UTC" }).format(new Date(sessionStorage.getItem('eDate')));
    const [value, setValue] = useState(0);
    const [cid] = useState(sessionStorage.getItem('rid'));
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [creativeData, setCreativeData] = useState([]);
    const [reportProfile, setreportProfile] = useState();
    const [data, setMetaData] = useState();
    const [campaignData, setCampaignData] = useState([]);
    const [devicesData, setDevicesData] = useState();
    const [adsetData, setAdsetData] = useState();
    const [engagementData, setEngagementData] = useState({ rows: [], totals: null });
    const token = useToken();
    const admintoken = parseInt(token.token.token);
    const [imageUrls, setImageUrls] = useState({});
    const { appVars, appLoading } = useContext(AppVarsContext);
    const firstCol = 275;
    const reportRef1 = useRef()
    const reportRef2 = useRef()
    const reportRef3 = useRef()
    const reportRef4 = useRef()
    const reportRef5 = useRef()
    const appReadyRef = useRef(false);
    const reportProfileRef = useRef(null);
    const fetchReportDataRef = useRef(null);
    const [conversionActions, setConversionActions] = useState([]);
    const [filtersOpen, setFiltersOpen] = useState(false);

    const normalizeConversionSpec = (value) => {
        if (!value) return [];
        if (Array.isArray(value)) {
            return value
                .map((entry) => {
                    if (entry == null) return null;
                    if (typeof entry === 'string' || typeof entry === 'number') return String(entry);
                    if (typeof entry === 'object') {
                        return entry.name || entry.label || entry.id || entry.value || entry.resourceName || entry.action_type || entry.conversion || null;
                    }
                    return null;
                })
                .filter(Boolean);
        }
        if (typeof value === 'object') {
            const nested =
                value?.conversions ||
                value?.conversion ||
                value?.conversion_actions ||
                value?.conversionActions ||
                value?.values ||
                [];
            return normalizeConversionSpec(nested);
        }
        return [];
    };

    const userSettings = useMemo(() => parseSettings(user?.settings), [user?.settings]);
    const reportSettings = useMemo(() => parseSettings(reportProfile?.settings), [reportProfile?.settings]);

    const defaultConversionSpec = useMemo(() => {
        const spec =
            pickMetaConversionSpec(userSettings) ||
            pickMetaConversionSpec(user) ||
            pickMetaConversionSpec(reportSettings) ||
            null;
        return normalizeConversionSpec(spec);
    }, [userSettings, user, reportSettings]);

    const conversionSessionKey = 'conversionSpec_meta';
    const conversionSessionRef = useRef({ has: false, value: [] });
    const loadSessionConversions = () => {
        const raw = sessionStorage.getItem(conversionSessionKey);
        if (raw === null) {
            conversionSessionRef.current = { has: false, value: [] };
            return conversionSessionRef.current;
        }
        try {
            const parsed = JSON.parse(raw);
            const normalized = normalizeConversionSpec(parsed);
            conversionSessionRef.current = { has: true, value: normalized };
            return conversionSessionRef.current;
        } catch (error) {
            conversionSessionRef.current = { has: true, value: [] };
            return conversionSessionRef.current;
        }
    };

    useEffect(() => {
        if (!reportProfile) return;
        const sessionConversions = loadSessionConversions();
        if (sessionConversions.has) {
            setConversionActions(sessionConversions.value);
            return;
        }
        if (defaultConversionSpec.length > 0) {
            setConversionActions(defaultConversionSpec);
        }
    }, [reportProfile, defaultConversionSpec]);






    //console.log('meta appVars',JSON.parse(appVars.tooltips).impression);
    const [client, setClient] = useState({
        clientId: sessionStorage.getItem('apiID'),
        apiPort: sessionStorage.getItem('aPort')
    });



    const fetchImageUrl = async (imageName) => {
        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/json");
        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: JSON.stringify({ "image": imageName }),
            redirect: "follow"
        };
        try {
            const response = await fetch(`${appVars.api_url}/meta/creatives/image`, requestOptions);
            const data = await response.json();
            return data.IMAGE_ASSET_URL;
        } catch (error) {
            console.error(error);
            return 'https://api.digitalwerks.com/imageFiles/no-image-icon.png'; // Default image on error
        }
    };








    const handleChange = (event, newValue) => {
        setValue(newValue);
        requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    };

    const brandTheme = useTheme();
    const theme = useMemo(() => createReportSurfaceTheme(brandTheme), [brandTheme]);
    const primaryColor = theme.palette.primary?.main || '#6aa7ff';
    const surfaceCardSx = getReportSurfaceSx(theme);
    const filterButtonSx = { minHeight: 44 };
    const tabsShellSx = { ...surfaceCardSx, px: 1 };
    const tabsSx = getAnalyticsReportTabsSx();
    const pageShellSx = getReportPageSx(theme);
    const displayReportProfile = useMemo(() => (
        reportProfile
            ? {
                ...reportProfile,
                showCost: false
            }
            : reportProfile
    ), [reportProfile]);


    async function fetchReportData(nextConversions) {
        setLoading(true);
        setLoadError('');
        props.setLoading?.(true);

        try {
            let conversions = Array.isArray(nextConversions) ? nextConversions : (conversionActions || []);
            conversions = normalizeConversionSpec(conversions);
            if (!conversions.length && defaultConversionSpec.length && !conversionSessionRef.current.has) {
                conversions = defaultConversionSpec;
            }

            const body = JSON.stringify({
                cid: sessionStorage.getItem('rid'),
                sdate: sessionStorage.getItem('sDate'),
                edate: sessionStorage.getItem('eDate'),
                conversions
            });
            const engagementBody = JSON.stringify({
                cid: sessionStorage.getItem('rid'),
                sdate: sessionStorage.getItem('sDate'),
                edate: sessionStorage.getItem('eDate')
            });

            const requestOptions = {
                method: 'POST',
                headers: { "Content-Type": "application/json" },
                body,
                redirect: 'follow'
            };
            const engagementRequestOptions = {
                method: 'POST',
                headers: { "Content-Type": "application/json" },
                body: engagementBody,
                redirect: 'follow'
            };

            const [
                metaDataRes,
                devicesDataRes,
                campaignRes,
                adsetsRes,
                creativesRes,
                engagementRes
            ] = await Promise.all([
                fetch(`${appVars.api_url}/meta/data`, requestOptions),
                fetch(`${appVars.api_url}/meta/devices`, requestOptions),
                fetch(`${appVars.api_url}/meta/campaigns`, requestOptions),
                fetch(`${appVars.api_url}/meta/adsets`, requestOptions),
                fetch(`${appVars.api_url}/meta/creatives`, requestOptions),
                fetch(`${appVars.api_url}/meta/engagement`, engagementRequestOptions).then(async (response) => ({
                    ok: response.ok,
                    data: await response.json().catch(() => ({}))
                }))
            ]);

            if ([metaDataRes, devicesDataRes, campaignRes, adsetsRes, creativesRes].some((response) => !response.ok)) throw new Error('Meta report request failed');

            const [
                metaData,
                devicesData,
                campaignData,
                adsetData,
                creativeData
            ] = await Promise.all([
                metaDataRes.json(),
                devicesDataRes.json(),
                campaignRes.json(),
                adsetsRes.json(),
                creativesRes.json()
            ]);

            setMetaData(metaData);
            setDevicesData(devicesData);
            setCampaignData(campaignData);
            setAdsetData(adsetData);
            setCreativeData(creativeData);
            setEngagementData(engagementRes.ok ? normalizeMetaEngagementPayload(engagementRes.data) : { rows: [], totals: null });

            console.log('meta data', creativeData);

            // preload creative image URLs
            if (creativeData[1]?.[0]) {
                await Promise.all(creativeData[1][0].map(async (item) => {
                    const imageUrl = await fetchImageUrl(item.NAME);
                    setImageUrls(prev => ({ ...prev, [item.NAME]: imageUrl }));
                }));
            }
            setLoading(false);
            //console.log('meta data', data);
        } catch (err) {
            console.error("Error fetching Meta report data:", err);
            setLoadError('Meta retail data could not load. Try again to reload this date range.');
        } finally {
            props.setLoading?.(false);
            setLoading(false);
        }
    }

    async function fetchProfile() {
        try {
            const rp = await fetch(`${appVars.api_url}/reportprofile/${sessionStorage.getItem('rid')}`, {
                method: "GET",
            });
            if (!rp.ok) throw new Error('Report profile request failed');
            const response = normalizeReportProfileVisibility(await rp.json());
            setreportProfile(response);
        } catch (error) {
            console.error('Error fetching report profile:', error);
            setLoadError('The report settings could not load. Try again to reopen this report.');
            setLoading(false);
        }
    }



    useEffect(() => {
        appReadyRef.current = !appLoading;
    }, [appLoading]);

    useEffect(() => {
        // console.log('Meta reportProfile changed:', reportProfile);
        reportProfileRef.current = reportProfile;
    }, [reportProfile]);

    useEffect(() => {
        fetchReportDataRef.current = fetchReportData;
    });

    useEffect(() => {
        const handleStorageChange = (e) => {
            console.log('Meta dc event detail:', e?.detail);
            if (!appReadyRef.current) return;
            if (!reportProfileRef.current) return;
            // Defer so sessionStorage updates settle before reading sDate/eDate.
            setTimeout(() => {
                try {
                    fetchReportDataRef.current?.();
                } catch (err) {
                    console.error('Meta dc refetch error:', err);
                }
            }, 0);
        };

        window.addEventListener('dc', handleStorageChange);
        return () => window.removeEventListener('dc', handleStorageChange);
    }, []);




    /**/
    useEffect(() => {
        if (!appLoading) { fetchProfile(); }


    }, [appLoading]);

    const hasFetchedRef = useRef(false);

    useEffect(() => {
        if (appLoading) return;
        if (!reportProfile) return;
        if (hasFetchedRef.current) return;

        hasFetchedRef.current = true;
        fetchReportData();
    }, [appLoading, reportProfile]);


    const buildTrendFromStats = (stats) => {

        if (!stats?.impressions?.series) return [];
        const keys = ['impressions', 'clicks', 'conversions', 'cost', 'revenue'];
        const map = new Map();
        stats.impressions.series.forEach((point) => {
            map.set(point.x, { date: point.x, impressions: point.y });
        });
        keys.forEach((key) => {
            if (!stats[key]?.series) return;
            stats[key].series.forEach((point) => {
                const row = map.get(point.x) || { date: point.x };
                row[key] = point.y;
                map.set(point.x, row);
            });
        });
        return Array.from(map.values()).sort((a, b) => String(a.date).localeCompare(String(b.date)));
    };

    const trendData = data ? buildTrendFromStats(data) : [];
    const deviceSummary = useMemo(() => {
        const rows = Array.isArray(devicesData) ? devicesData : [];
        return rows
            .map((row) => {
                const deviceKey = normalizeDeviceKey(row?.DEVICE || row?.device);
                const label = deviceLabelMap[deviceKey] || row?.DEVICE || row?.device || 'Unknown';
                const impressions = Number(row?.IMPRESSIONS ?? row?.impressions ?? 0);
                const clicks = Number(row?.CLICKS ?? row?.clicks ?? 0);
                const conversions = Number(row?.CONVERSIONS ?? row?.conversions ?? 0);
                const cost = Number(row?.COST ?? row?.cost ?? 0);
                const revenue = Number(row?.REVENUE ?? row?.revenue ?? 0);
                const ctr = Number(row?.CTR ?? row?.ctr ?? (impressions > 0 ? (clicks / impressions) * 100 : 0));
                const convRate = Number(row?.CONV_RATE ?? row?.CONVRATE ?? row?.convrate ?? (clicks > 0 ? (conversions / clicks) * 100 : 0));
                const roas = cost > 0 ? revenue / cost : 0;
                return {
                    deviceKey,
                    label,
                    impressions,
                    clicks,
                    conversions,
                    cost,
                    revenue,
                    ctr,
                    convRate,
                    roas
                };
            })
            .sort((a, b) => (b.impressions || 0) - (a.impressions || 0));
    }, [devicesData]);

    const deviceTotals = useMemo(() => {
        return deviceSummary.reduce(
            (acc, row) => {
                acc.impressions += row.impressions || 0;
                acc.clicks += row.clicks || 0;
                acc.conversions += row.conversions || 0;
                acc.cost += row.cost || 0;
                acc.revenue += row.revenue || 0;
                return acc;
            },
            { impressions: 0, clicks: 0, conversions: 0, cost: 0, revenue: 0 }
        );
    }, [deviceSummary]);

    const deviceMixBasis = deviceTotals.impressions > 0 ? 'impressions' : 'clicks';
    const deviceLabels = deviceSummary.map((row) => row.label);
    const deviceMixSeries = deviceSummary.map((row) => row[deviceMixBasis] || 0);
    const deviceReachSeries = [
        { name: 'Impressions', data: deviceSummary.map((row) => row.impressions || 0) },
        { name: 'Clicks', data: deviceSummary.map((row) => row.clicks || 0) }
    ];
    const deviceRateSeries = [
        { name: 'CTR', data: deviceSummary.map((row) => row.ctr || 0) },
        { name: 'Conv Rate', data: deviceSummary.map((row) => row.convRate || 0) }
    ];

    const deviceMixOptions = useMemo(() => ({
        labels: deviceLabels,
        chart: { type: 'donut', zoom: { enabled: false }, toolbar: { show: false } },
        legend: { position: 'bottom' },
        dataLabels: { enabled: false },
        colors: reportColors.categories,
        plotOptions: {
            pie: {
                donut: {
                    size: '60%',
                    labels: {
                        show: true,
                        total: {
                            show: true,
                            label: deviceMixBasis === 'impressions' ? 'Impressions' : 'Clicks',
                            formatter: () => formatNumber(deviceTotals[deviceMixBasis])
                        }
                    }
                }
            }
        },
        tooltip: {
            y: { formatter: (val) => formatNumber(val) }
        }
    }), [deviceLabels, deviceMixBasis, deviceTotals]);

    const deviceReachOptions = useMemo(() => ({
        chart: { type: 'bar', zoom: { enabled: false }, toolbar: { show: false } },
        plotOptions: { bar: { columnWidth: '50%', borderRadius: 6 } },
        dataLabels: { enabled: false },
        legend: { position: 'top' },
        colors: [reportColors.revenue, reportColors.conversions],
        xaxis: { categories: deviceLabels, labels: { rotate: -15 } },
        yaxis: {
            labels: { formatter: (val) => formatNumber(val) }
        },
        tooltip: {
            y: { formatter: (val) => formatNumber(val) }
        }
    }), [deviceLabels]);

    const deviceRateOptions = useMemo(() => ({
        chart: { type: 'bar', zoom: { enabled: false }, toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, barHeight: '55%', borderRadius: 6 } },
        dataLabels: { enabled: false },
        legend: { position: 'top' },
        colors: [reportColors.revenue, reportColors.conversions],
        xaxis: {
            categories: deviceLabels,
            labels: { formatter: (val) => `${val}%` }
        },
        tooltip: {
            y: { formatter: (val) => formatPercent(val) }
        }
    }), [deviceLabels]);

    const audienceRows = useMemo(() => {
        if (!Array.isArray(creativeData)) return [];
        const flattened = creativeData.reduce((acc, item) => acc.concat(Array.isArray(item) ? item : [item]), []);
        return flattened.filter((row) => row && typeof row === 'object' && (row.CREATIVE || row.AUDIENCE || row.CAMPAIGN || row.IMPRESSIONS !== undefined));
    }, [creativeData]);

    const audienceSummary = useMemo(() => {
        return audienceRows.map((row) => buildMetaSummaryRow(row, ['CREATIVE', 'AUDIENCE', 'CAMPAIGN', 'NAME']));
    }, [audienceRows]);

    const audienceTotals = useMemo(() => {
        return audienceSummary.reduce(
            (acc, row) => {
                acc.impressions += row.impressions;
                acc.clicks += row.clicks;
                acc.conversions += row.conversions;
                acc.cost += row.cost;
                acc.revenue += row.revenue;
                return acc;
            },
            { impressions: 0, clicks: 0, conversions: 0, cost: 0, revenue: 0 }
        );
    }, [audienceSummary]);

    const audienceTopClicks = useMemo(() => {
        return audienceSummary
            .slice()
            .sort((a, b) => b.clicks - a.clicks)
            .slice(0, 8);
    }, [audienceSummary]);

    const audienceTopImpressions = useMemo(() => {
        return audienceSummary
            .slice()
            .sort((a, b) => b.impressions - a.impressions)
            .slice(0, 8);
    }, [audienceSummary]);

    const audienceClickOptions = useMemo(() => ({
        chart: { type: 'bar', zoom: { enabled: false }, toolbar: { show: false } },
        plotOptions: { bar: { columnWidth: '55%', borderRadius: 6 } },
        dataLabels: { enabled: false },
        colors: [reportColors.revenue],
        xaxis: { categories: audienceTopClicks.map((row) => row.label), labels: { rotate: -15 } },
        tooltip: { y: { formatter: (val) => formatNumber(val) } }
    }), [audienceTopClicks]);

    const audienceClickSeries = [{ name: 'Clicks', data: audienceTopClicks.map((row) => row.clicks) }];

    const audienceImpressionOptions = useMemo(() => ({
        chart: { type: 'bar', zoom: { enabled: false }, toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, barHeight: '55%', borderRadius: 6 } },
        dataLabels: { enabled: false },
        colors: [reportColors.conversions],
        xaxis: { categories: audienceTopImpressions.map((row) => row.label), labels: { formatter: (val) => formatNumber(val) } },
        tooltip: { y: { formatter: (val) => formatNumber(val) } }
    }), [audienceTopImpressions]);

    const audienceImpressionSeries = [{ name: 'Impressions', data: audienceTopImpressions.map((row) => row.impressions) }];

    const overallAudienceCtr = audienceTotals.impressions > 0 ? (audienceTotals.clicks / audienceTotals.impressions) * 100 : 0;
    const overallAudienceConvRate = audienceTotals.clicks > 0 ? (audienceTotals.conversions / audienceTotals.clicks) * 100 : 0;

    const campaignSummary = useMemo(() => {
        const rows = Array.isArray(campaignData) ? campaignData : [];
        return rows.map((row) => buildMetaSummaryRow(row, ['CAMPAIGN', 'CAMPAIGN_NAME', 'NAME']));
    }, [campaignData]);

    const adSetSummary = useMemo(() => {
        const rows = Array.isArray(adsetData) ? adsetData : [];
        return rows.map((row) => buildMetaSummaryRow(row, ['ADSET', 'AD_SET', 'ADSET_NAME', 'AD_SET_NAME', 'NAME', 'CAMPAIGN']));
    }, [adsetData]);

    const adSetTopImpressions = useMemo(() => (
        adSetSummary.slice().sort((a, b) => b.impressions - a.impressions).slice(0, 8)
    ), [adSetSummary]);

    const adSetTopClicks = useMemo(() => (
        adSetSummary.slice().sort((a, b) => b.clicks - a.clicks).slice(0, 8)
    ), [adSetSummary]);

    const adSetTopCtr = useMemo(() => {
        return adSetSummary
            .slice()
            .filter((row) => row.impressions > 0 || row.clicks > 0)
            .sort((a, b) => b.ctr - a.ctr)
            .slice(0, 8);
    }, [adSetSummary]);

    const adSetTopConversions = useMemo(() => (
        adSetSummary.slice().sort((a, b) => b.conversions - a.conversions).slice(0, 8)
    ), [adSetSummary]);

    const adSetWatchlist = useMemo(() => {
        const highReach = adSetSummary.filter((row) => row.impressions > 0);
        if (!highReach.length) return [];
        const sortedByReach = highReach.slice().sort((a, b) => b.impressions - a.impressions);
        const thresholdIndex = Math.max(0, Math.floor(sortedByReach.length * 0.5) - 1);
        const reachThreshold = sortedByReach[thresholdIndex]?.impressions ?? 0;
        return highReach
            .filter((row) => row.impressions >= reachThreshold)
            .sort((a, b) => a.ctr - b.ctr)
            .slice(0, 3);
    }, [adSetSummary]);

    const adSetReachOptions = useMemo(() => ({
        chart: { type: 'bar', zoom: { enabled: false }, toolbar: { show: false } },
        plotOptions: { bar: { columnWidth: '55%', borderRadius: 6 } },
        dataLabels: { enabled: false },
        colors: [reportColors.revenue, reportColors.conversions],
        xaxis: { categories: adSetTopImpressions.map((row) => truncateText(row.label, 32)), labels: { rotate: -15 } },
        tooltip: { y: { formatter: (val) => formatNumber(val) } }
    }), [adSetTopImpressions]);

    const adSetReachSeries = [
        { name: 'Impressions', data: adSetTopImpressions.map((row) => row.impressions) },
        { name: 'Clicks', data: adSetTopImpressions.map((row) => row.clicks) }
    ];

    const adSetCtrOptions = useMemo(() => ({
        chart: { type: 'bar', zoom: { enabled: false }, toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, barHeight: '55%', borderRadius: 6 } },
        dataLabels: { enabled: false },
        colors: [reportColors.revenue],
        xaxis: { categories: adSetTopCtr.map((row) => truncateText(row.label, 32)), labels: { formatter: (val) => `${Number(val).toFixed(2)}%` } },
        tooltip: { y: { formatter: (val) => `${Number(val).toFixed(2)}%` } }
    }), [adSetTopCtr]);

    const adSetCtrSeries = [{ name: 'CTR', data: adSetTopCtr.map((row) => row.ctr) }];

    const adSetConversionOptions = useMemo(() => ({
        chart: { type: 'bar', zoom: { enabled: false }, toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, barHeight: '55%', borderRadius: 6 } },
        dataLabels: { enabled: false },
        colors: [reportColors.conversions],
        xaxis: { categories: adSetTopConversions.map((row) => truncateText(row.label, 32)), labels: { formatter: (val) => formatNumber(val) } },
        tooltip: { y: { formatter: (val) => formatNumber(val) } }
    }), [adSetTopConversions]);

    const adSetConversionSeries = [{ name: 'Conversions', data: adSetTopConversions.map((row) => row.conversions) }];

    const engagementRows = useMemo(() => (
        Array.isArray(engagementData?.rows) ? engagementData.rows : []
    ), [engagementData]);

    const engagementTotals = useMemo(() => (
        engagementData?.totals || {
            impressions: 0,
            clicks: 0,
            engagementTotal: 0,
            reactions: 0,
            comments: 0,
            shares: 0,
            saves: 0
        }
    ), [engagementData]);

    const overviewHeroChips = useMemo(() => (
        [
            { label: 'Ad Sets', value: adSetSummary.length },
            ...(campaignSummary.length ? [{ label: 'Campaigns', value: campaignSummary.length }] : []),
            { label: 'Audiences', value: audienceSummary.length },
            { label: 'Devices', value: Array.isArray(devicesData) ? devicesData.length : 0 },
            ...(adSetTopImpressions[0]?.label ? [{ label: 'Top Ad Set', value: truncateText(adSetTopImpressions[0].label) }] : []),
            ...(audienceTopClicks[0]?.label ? [{ label: 'Top Audience', value: truncateText(audienceTopClicks[0].label) }] : [])
        ]
    ), [adSetSummary.length, adSetTopImpressions, audienceSummary.length, audienceTopClicks, campaignSummary.length, devicesData]);
    return (
        <ThemeProvider theme={theme}><Box sx={pageShellSx}>
            <Box sx={{ position: 'relative', display: 'grid', gap: 1.5 }}>
                <Box sx={tabsShellSx}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: { xs: 'stretch', sm: 'center' },
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 1,
                            width: '100%'
                        }}
                    >
                        <ResponsiveTabs
                            value={value}
                            onChange={handleChange}
                            indicatorColor="primary"
                            aria-label="Meta retail report tabs"
                            variant="standard"
                            scrollButtons="auto"
                            sx={{ ...tabsSx, flex: '1 1 320px' }}
                        >
                                <Tab label="Performance Overview" {...a11yProps(0)} />
                                <Tab label="Ad Sets" {...a11yProps(1)} />
                                <Tab label="Audiences" {...a11yProps(2)} />
                                <Tab label="Engagement" {...a11yProps(3)} />
                                <Tab label="Device Info" {...a11yProps(4)} />
                                <Tab label="Notes" {...a11yProps(5)} />
                            </ResponsiveTabs>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, ml: 'auto', flex: '0 0 auto' }}>
                            <ReportToolbarButton
                                onClick={() => setFiltersOpen(true)}
                                startIcon={<MdFilterAlt />}
                            >
                                Filter
                            </ReportToolbarButton>
                        </Box>
                    </Box>
                </Box>

                {loadError ? <Alert severity="error" action={<Button color="inherit" onClick={() => reportProfile ? fetchReportData() : fetchProfile()}>Try again</Button>}>{loadError}</Alert> : null}
                {!loading && !loadError && data && !(data.impressions?.count ?? data.impressions) && !(data.clicks?.count ?? data.clicks) ? <Alert severity="info">No Meta activity was returned for this date range. Choose a wider date range to review earlier activity.</Alert> : null}
                {value !== 0 ? <ReportSectionIntro report="Meta retail" title={['Performance', 'Ad sets', 'Audiences', 'Engagement', 'Devices', 'Notes'][value]} description={['', 'Compare delivery and response across your ad sets.', 'Review audience activity and click-through rates.', 'Explore reactions, comments, shares, and saves.', 'See which devices generate exposure and traffic.', 'Keep context alongside your retail report.'][value]} dateLabel={pDate} /> : null}
                {value === 0 ? <ReportPerformanceOverview
                    eyebrow="Meta retail"
                    title="From attention to response"
                    description="See how retail advertising becomes traffic and recorded actions."

                    loading={loading && !data}
                    stages={[
                        { label: 'Visibility', description: 'Exposure to your retail advertising.', metrics: [
                            { label: 'Impressions', value: loadError && !data ? '—' : formatNumber(data?.impressions?.count ?? data?.impressions), detail: 'Delivered impressions' },
                            { label: 'CTR', value: loadError && !data ? '—' : formatPercent(data?.ctr?.count ?? data?.ctr), detail: 'Clicks / impressions' }
                        ] },
                        { label: 'Traffic', description: 'Traffic and audience activity.', metrics: [
                            { label: 'Clicks', value: loadError && !data ? '—' : formatNumber(data?.clicks?.count ?? data?.clicks), detail: 'Traffic generated' },
                            { label: 'Audiences', value: formatNumber(audienceSummary.length), detail: 'Audiences in this report' }
                        ] },
                        { label: 'Response', description: 'Recorded actions following a click.', metrics: [
                            { label: 'Conversions', value: loadError && !data ? '—' : formatNumber(data?.conversions?.count ?? data?.conversions), detail: 'Recorded conversions' },
                            { label: 'Conversion rate', value: loadError && !data ? '—' : formatPercent(data?.convrate?.count ?? data?.convrate), detail: 'Conversions / clicks' }
                        ] }
                    ]}
                    highlights={overviewHeroChips.map((chip) => ({ label: chip.label, title: String(chip.value), detail: 'Within the selected date range' }))}
                /> : null}

            <Dialog
                open={filtersOpen}
                onClose={() => setFiltersOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: surfaceCardSx
                }}
            >
                <DialogTitle
                    sx={{
                        background: theme.palette.background.paper,
                        borderBottom: `1px solid ${alpha(primaryColor, 0.12)}`
                    }}
                >
                    Report Filters
                </DialogTitle>
                <DialogContent sx={{ display: 'grid', gap: 2, pt: 2, mt: 2 }}>
                    <ReportConversionSelect
                        service="meta"
                        value={conversionActions}
                        onChange={(next) => {
                            const normalized = normalizeConversionSpec(next);
                            setConversionActions(normalized);
                            sessionStorage.setItem(conversionSessionKey, JSON.stringify(normalized));
                            conversionSessionRef.current = { has: true, value: normalized };
                            fetchReportData(normalized);
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setFiltersOpen(false)} sx={filterButtonSx}>Close</Button>
                </DialogActions>
            </Dialog>
                <TabPanel value={value} index={0} data-title="Performance Overview">
                    <div ref={reportRef1}>
                        <Box sx={{ mt: 0 }}>
                            {trendData && Array.isArray(trendData) && trendData.length > 0 ? (
                                <Box sx={{ ...surfaceCardSx, p: { xs: 1, md: 1.25 } }}>
                                    <TrendChart
                                        reportStyle
                                        trendData={trendData}
                                        showCost={false}
                                        showRevenue={false}
                                        theme={theme}
                                        noteType="meta"
                                        noteView="a7edd5c0-7a77-49ed-86ad-6fe3066a0f59"
                                    />
                                </Box>
                            ) : loading ? <Skeleton variant="rectangular" width={'100%'} height={240} sx={{ ...surfaceCardSx, mt: 0 }} /> : <Alert severity="info">No trend data for this date range. Try a wider date range.</Alert>}
                        </Box>
                    </div>
                </TabPanel>
                <TabPanel value={value} index={1} data-title="Ad Sets">
                    <div>
                        <Box sx={{ mt: 2, display: 'grid', gap: 2 }}>
                            {loading ? (
                                <Skeleton variant="rectangular" width={'100%'} height={240} sx={surfaceCardSx} />
                            ) : adSetSummary.length ? (
                                <Box className="vimarc-meta-table" sx={{ overflowX: 'auto' }}>
                                    <ViMarcTableCard
                                        instanceKey="adsets"
                                        active={value === 1}
                                        title="Ad Sets"
                                        data={adSetSummary}
                                        count={(adSetSummary.length > 10 ? 10 : adSetSummary.length)}
                                        reportProfile={displayReportProfile}
                                        themeColor={{
                                            primary: theme.palette.primary.main,
                                            secondary: theme.palette.primary.main
                                        }}
                                        sx={{ width: '100%' }}
                                        columns={[
                                            {
                                                Header: ({ column }) => (
                                                    <Tooltip title={
                                                        <div>
                                                            <Typography fontWeight={900}>Ad Set</Typography>
                                                            <Typography>{'Ad sets being reported'}</Typography>
                                                        </div>
                                                    }>
                                                        <span>Ad Set</span>
                                                    </Tooltip>
                                                ),
                                                header: 'Ad Set',
                                                accessorKey: "label",
                                                grow: 1,
                                                size: 300,
                                                Cell: ({ cell }) => (
                                                    <Typography textAlign="left" fontSize={13} fontWeight={900}>
                                                        {cell.row.original.label}
                                                    </Typography>
                                                ),
                                                Footer: () => (
                                                    <Typography fontSize={13} fontWeight={900} textAlign="left">
                                                        Totals
                                                    </Typography>
                                                ),
                                            }
                                        ]}
                                    />
                                </Box>
                            ) : (
                                <Typography variant="body2" color="text.secondary" align="center">
                                    No ad set data available for this range.
                                </Typography>
                            )}

                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                    Ad Set Insights
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Reach, click volume, and awareness quality across Meta ad sets.
                                </Typography>
                            </Box>

                            {loading ? (
                                <Skeleton variant="rectangular" height={260} sx={surfaceCardSx} />
                            ) : adSetSummary.length ? (
                                <>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, gap: 1 }}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="caption" color="text.secondary">Highest Reach</Typography>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                    {adSetTopImpressions[0]?.label || '—'}
                                                </Typography>
                                                <Typography variant="body2">{formatNumber(adSetTopImpressions[0]?.impressions || 0)} impressions</Typography>
                                            </CardContent>
                                        </Card>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="caption" color="text.secondary">Most Clicks</Typography>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                    {adSetTopClicks[0]?.label || '—'}
                                                </Typography>
                                                <Typography variant="body2">
                                                    {formatNumber(adSetTopClicks[0]?.clicks || 0)} clicks
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="caption" color="text.secondary">Best CTR</Typography>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                    {adSetTopCtr[0]?.label || '—'}
                                                </Typography>
                                                <Typography variant="body2">
                                                    {adSetTopCtr[0] ? formatPercent(adSetTopCtr[0].ctr) : '—'}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                        <Card variant="outlined" sx={{ borderLeft: `4px solid ${theme.palette.error.main}` }}>
                                            <CardContent>
                                                <Typography variant="caption" color="text.secondary">Watchlist</Typography>
                                                {adSetWatchlist.length ? (
                                                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                                        {adSetWatchlist.map((row) => (
                                                            <Typography key={row.label} variant="body2">
                                                                {row.label} • CTR {formatPercent(row.ctr)}
                                                            </Typography>
                                                        ))}
                                                    </Stack>
                                                ) : (
                                                    <Typography variant="body2">—</Typography>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </Box>

                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.4fr 1fr' }, gap: 2 }}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                                    Top Ad Set Reach
                                                </Typography>
                                                {adSetTopImpressions.length ? (
                                                    <ReactApexChart options={ensureApexOptions(adSetReachOptions, 'bar', theme)} series={adSetReachSeries} type="bar" height={300} />
                                                ) : (
                                                    loading ? <Skeleton variant="rectangular" height={260} sx={surfaceCardSx} /> : <Typography color="text.secondary">No activity for this date range.</Typography>
                                                )}
                                            </CardContent>
                                        </Card>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                                    Top CTR Ad Sets
                                                </Typography>
                                                {adSetTopCtr.length ? (
                                                    <ReactApexChart options={ensureApexOptions(adSetCtrOptions, 'bar', theme)} series={adSetCtrSeries} type="bar" height={300} />
                                                ) : (
                                                    loading ? <Skeleton variant="rectangular" height={260} sx={surfaceCardSx} /> : <Typography color="text.secondary">No activity for this date range.</Typography>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </Box>

                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                                Most Converting Ad Sets
                                            </Typography>
                                            {adSetTopConversions.length ? (
                                                <ReactApexChart options={ensureApexOptions(adSetConversionOptions, 'bar', theme)} series={adSetConversionSeries} type="bar" height={280} />
                                            ) : (
                                                loading ? <Skeleton variant="rectangular" height={240} sx={surfaceCardSx} /> : <Typography color="text.secondary">No activity for this date range.</Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                </>
                            ) : (
                                <Typography variant="body2" color="text.secondary" align="center">
                                    No ad set insights are available for this range.
                                </Typography>
                            )}
                        </Box>
                    </div>
                </TabPanel>
                <TabPanel value={value} index={2} data-title="Audiences">

                    <div ref={reportRef2}>
                        <Box sx={{ mt: 1, display: 'grid', gap: 2 }}>
                            {loading ? (
                                <Box sx={{ display: 'grid', gap: 2 }}>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(5, minmax(0, 1fr))' }, gap: 1 }}>
                                        {Array.from({ length: 5 }).map((_, index) => (
                                            <Skeleton key={`audience-kpi-${index}`} variant="rectangular" height={90} sx={surfaceCardSx} />
                                        ))}
                                    </Box>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.2fr 1fr' }, gap: 2 }}>
                                        <Skeleton variant="rectangular" height={260} sx={surfaceCardSx} />
                                        <Skeleton variant="rectangular" height={260} sx={surfaceCardSx} />
                                    </Box>
                                    <Skeleton variant="rectangular" height={260} sx={surfaceCardSx} />
                                </Box>
                            ) : audienceSummary.length ? (
                                <>
                                    <Box className="vimarc-meta-table" sx={{ overflowX: 'auto' }}>
                                        <ViMarcTableCard
                                            instanceKey="audiences"
                                            active={value === 2}
                                            getRowId={(r) => r.ID ?? r.id ?? `${r.CREATIVE}-${r.CAMPAIGN ?? ''}`}
                                            title=""
                                            data={audienceRows}
                                            count={(audienceRows.length > 10 ? 10 : audienceRows.length)}
                                            client={props.client}
                                            reportProfile={displayReportProfile}
                                            columns={[
                                                {
                                                    Header: ({ column }) => (
                                                        <Tooltip title={<div><Typography fontWeight={900}>Audience</Typography><Typography>{'Audiences being reported'}</Typography></div>}>
                                                            <span>Audience</span>
                                                        </Tooltip>
                                                    ),
                                                    header: 'Audience',
                                                    accessorKey: "CREATIVE",
                                                    grow: 1,
                                                    size: 300,
                                                    Cell: ({ cell }) => (
                                                        <Typography textAlign={'right'} fontSize={13} fontWeight={900} >
                                                            {cell.row.original.CREATIVE}
                                                        </Typography>
                                                    ),
                                                    Footer: () => <Typography fontSize={13} fontWeight={900} textAlign={'right'}>Totals</Typography>,
                                                }
                                            ]}
                                            renderDetailPanel={({ row }) => (
                                                <TableCell sx={{ p: 0, m: 0 }}>
                                                    <CreativeDetailTabs
                                                        rowData={row.original}
                                                        appVars={appVars}
                                                        sDate={props.sDate}
                                                        eDate={props.eDate}
                                                        fetchImageUrl={fetchImageUrl}
                                                        imageUrls={imageUrls}
                                                        setImageUrls={setImageUrls}
                                                        reportProfile={displayReportProfile}
                                                    />
                                                </TableCell>
                                            )}

                                        />
                                    </Box>

                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(5, minmax(0, 1fr))' }, gap: 1 }}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="caption" color="text.secondary">Total Impressions</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 700 }}>{formatNumber(audienceTotals.impressions)}</Typography>
                                            </CardContent>
                                        </Card>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="caption" color="text.secondary">Total Clicks</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 700 }}>{formatNumber(audienceTotals.clicks)}</Typography>
                                            </CardContent>
                                        </Card>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="caption" color="text.secondary">CTR</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 700 }}>{formatPercent(overallAudienceCtr)}</Typography>
                                            </CardContent>
                                        </Card>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="caption" color="text.secondary">Conversions</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 700 }}>{formatNumber(audienceTotals.conversions)}</Typography>
                                            </CardContent>
                                        </Card>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="caption" color="text.secondary">Conversion Rate</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 700 }}>{formatPercent(overallAudienceConvRate)}</Typography>
                                            </CardContent>
                                        </Card>
                                    </Box>

                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.2fr 1fr' }, gap: 2 }}>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                                    Top Audiences by Clicks
                                                </Typography>
                                                {audienceTopClicks.length ? (
                                                    <ReactApexChart options={ensureApexOptions(audienceClickOptions, 'bar', theme)} series={audienceClickSeries} type="bar" height={280} />
                                                ) : (
                                                    loading ? <Skeleton variant="rectangular" height={240} sx={surfaceCardSx} /> : <Typography color="text.secondary">No activity for this date range.</Typography>
                                                )}
                                            </CardContent>
                                        </Card>
                                        <Card variant="outlined">
                                            <CardContent>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                                    Top Audiences by Impressions
                                                </Typography>
                                                {audienceTopImpressions.length ? (
                                                    <ReactApexChart options={ensureApexOptions(audienceImpressionOptions, 'bar', theme)} series={audienceImpressionSeries} type="bar" height={280} />
                                                ) : (
                                                    loading ? <Skeleton variant="rectangular" height={240} sx={surfaceCardSx} /> : <Typography color="text.secondary">No activity for this date range.</Typography>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </Box>
                                </>
                            ) : (
                                <Typography variant="body2" color="text.secondary" align="center">
                                    No audience data available for this range.
                                </Typography>
                            )}
                        </Box>
                    </div>
                </TabPanel>
                <TabPanel ref={reportRef3} value={value} index={3} data-title="Engagement" >
                    <div ref={reportRef3}>
                        <Box sx={{ mt: 1, display: 'grid', gap: 2 }}>
                            <MetaEngagementSection
                                reportStyle
                                loading={loading}
                                rows={engagementRows}
                                totals={engagementTotals}
                                theme={theme}
                                cardSx={surfaceCardSx}
                                entityLabel="Ad Set"
                                emptyMessage="No engagement metrics were returned for this date range."
                                table={(
                                    <Box className="vimarc-meta-table" sx={{ overflowX: 'auto' }}>
                                        <ViMarcTableCard
                                            instanceKey="engagement"
                                            active={value === 3}
                                            title="Engagement"
                                            data={engagementRows}
                                            count={(engagementRows.length > 10 ? 10 : engagementRows.length)}
                                            reportProfile={displayReportProfile}
                                            appendDefaultMetrics={false}
                                            columns={getMetaEngagementColumns({ entityLabel: 'Ad Set', totals: engagementTotals })}
                                        />
                                    </Box>
                                )}
                            />
                        </Box>
                    </div>
                </TabPanel>
                <TabPanel value={value} index={4} data-title="Device Info" >
         
                    {(devicesData?.length ?
                        <Box sx={{ mt: 1, display: 'grid', gap: 2 }}>
                            <Box className="vimarc-meta-table" sx={{ overflowX: 'auto' }}>
                                <ViMarcTableCard
                                    ref={reportRef4}
                                    instanceKey="devices"
                                    active={value === 4}
                                    data={devicesData}
                                    count={(devicesData.length > 10 ? 10 : devicesData.length)}
                                    client={props.client}
                                    reportProfile={displayReportProfile}
                                    themeColor={{
                                        primary: theme.palette.primary.main,
                                        secondary: theme.palette.primary.main
                                    }}
                                    columns={[
                                        {
                                            Header: ({ column }) => (
                                                <Tooltip title={<div><Typography fontWeight={900}>Device</Typography><Typography>{'Devices being reported'}</Typography></div>}>
                                                    <span>Device</span>
                                                </Tooltip>
                                            ),
                                            header: 'Device',
                                            accessorKey: "DEVICE",
                                            grow: 1,
                                            size: 140,
                                            Cell: ({ cell }) => {
                                                const rawDevice = cell.row.original.DEVICE;
                                                const deviceKey = normalizeDeviceKey(rawDevice);
                                                const deviceLabel = deviceLabelMap[deviceKey] || rawDevice || 'Unknown';
                                                const impressions = Number(cell.row.original.IMPRESSIONS ?? 0);
                                                const clicks = Number(cell.row.original.CLICKS ?? 0);
                                                const shareBase = deviceTotals.impressions > 0 ? deviceTotals.impressions : deviceTotals.clicks;
                                                const shareValue = shareBase > 0 ? ((deviceTotals.impressions > 0 ? impressions : clicks) / shareBase) * 100 : 0;

                                                let icon = <IoDesktop size={20} />;
                                                switch (deviceKey) {
                                                    case 'iphone':
                                                        icon = (
                                                            <Box>
                                                                <IoLogoApple size={18} />
                                                                <MdPhoneIphone size={18} />
                                                            </Box>
                                                        );
                                                        break;
                                                    case 'android_smartphone':
                                                        icon = (
                                                            <Box>
                                                                <FcAndroidOs size={18} />
                                                                <MdOutlinePhoneAndroid size={18} />
                                                            </Box>
                                                        );
                                                        break;
                                                    case 'android_tablet':
                                                        icon = (
                                                            <Box>
                                                                <FcAndroidOs size={18} />
                                                                <MdTabletAndroid size={18} />
                                                            </Box>
                                                        );
                                                        break;
                                                    case 'ipad':
                                                        icon = (
                                                            <Box>
                                                                <IoLogoApple size={18} />
                                                                <IoIosTabletLandscape size={18} />
                                                            </Box>
                                                        );
                                                        break;
                                                    case 'desktop':
                                                        icon = <IoDesktop size={18} />;
                                                        break;
                                                    default:
                                                        icon = <IoDesktop size={18} />;
                                                        break;
                                                }

                                                return (
                                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: firstCol }}>
                                                        <Box sx={{ minWidth: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            {icon}
                                                        </Box>
                                                        <Box>
                                                            <Typography fontSize={13} fontWeight={700}>
                                                                {deviceLabel}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {shareValue.toFixed(1)}% of {deviceTotals.impressions > 0 ? 'impressions' : 'clicks'}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                );
                                            },
                                            Footer: () => <Typography fontSize={13} fontWeight={900} textAlign={'right'}>Totals</Typography>,
                                        }
                                    ]}
                                />
                            </Box>

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.1fr 1.4fr' }, gap: 2 }}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                            Device Mix ({deviceMixBasis === 'impressions' ? 'Impressions' : 'Clicks'})
                                        </Typography>
                                        {deviceSummary.length ? (
                                            <ReactApexChart options={ensureApexOptions(deviceMixOptions, 'donut', theme)} series={deviceMixSeries} type="donut" height={300} />
                                        ) : (
                                            loading ? <Skeleton variant="rectangular" height={260} sx={surfaceCardSx} /> : <Typography color="text.secondary">No activity for this date range.</Typography>
                                        )}
                                    </CardContent>
                                </Card>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                            Clicks vs Impressions by Device
                                        </Typography>
                                        {deviceSummary.length ? (
                                            <ReactApexChart options={ensureApexOptions(deviceReachOptions, 'bar', theme)} series={deviceReachSeries} type="bar" height={300} />
                                        ) : (
                                            loading ? <Skeleton variant="rectangular" height={260} sx={surfaceCardSx} /> : <Typography color="text.secondary">No activity for this date range.</Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.2fr 1fr' }, gap: 2 }}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                            Efficiency by Device (CTR vs Conversion Rate)
                                        </Typography>
                                        {deviceSummary.length ? (
                                            <ReactApexChart options={ensureApexOptions(deviceRateOptions, 'bar', theme)} series={deviceRateSeries} type="bar" height={320} />
                                        ) : (
                                            loading ? <Skeleton variant="rectangular" height={260} sx={surfaceCardSx} /> : <Typography color="text.secondary">No activity for this date range.</Typography>
                                        )}
                                    </CardContent>
                                </Card>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                            Device Click Share
                                        </Typography>
                                        {deviceSummary.length ? (
                                            <ReactApexChart
                                                options={ensureApexOptions({
                                                    ...deviceMixOptions,
                                                    plotOptions: {
                                                        pie: {
                                                            donut: {
                                                                size: '60%',
                                                                labels: {
                                                                    show: true,
                                                                    total: {
                                                                        show: true,
                                                                        label: 'Clicks',
                                                                        formatter: () => formatNumber(deviceTotals.clicks)
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }
                                                }, 'donut', theme)}
                                                series={deviceSummary.map((row) => row.clicks || 0)}
                                                type="donut"
                                                height={320}
                                            />
                                        ) : (
                                            loading ? <Skeleton variant="rectangular" height={260} sx={surfaceCardSx} /> : <Typography color="text.secondary">No activity for this date range.</Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Box>
                        </Box>
                        : loading ? <Skeleton variant="rectangular" height={260} sx={surfaceCardSx} /> : <Alert severity="info">No device data for this date range. Try a wider date range.</Alert>)}


                </TabPanel>
                <TabPanel ref={reportRef5} value={value} index={5}>

                    <Comments eDate={props.eDate} sDate={props.sDate} color={theme.palette.primary.main} admintoken={admintoken} view={(admintoken >= 6 ? "5af0a295-8b57-4ccb-8cc8-d54e5bb278e6" : "a7edd5c0-7a77-49ed-86ad-6fe3066a0f59")} type="meta" setLoading={props.setLoading}></Comments>
                </TabPanel>
            </Box>
        </Box></ThemeProvider>
    );
};

Meta.propTypes = {

};

export default Meta;
